import merge from 'lodash/merge';
import ReactApexChart from 'react-apexcharts';
// @mui
import { styled } from '@mui/material/styles';
import { CardHeader, CardProps } from '@mui/material';
// utils
import { fNumber } from '../../../../../utils/formatNumber';
// components
import { BaseOptionChart } from '../../../../../components/chart';
import { useMemo } from 'react';
import { CircularProgress } from '@mui/material';

// ----------------------------------------------------------------------

const ChartWrapperStyle = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden', // Keep content within card boundaries
  minHeight: 0,
  '& .apexcharts-canvas': {
    margin: '0 auto',
    overflow: 'hidden',
    maxWidth: '100%',
  },
  '& svg': {
    overflow: 'hidden',
    maxWidth: '100%',
    height: 'auto',
  },
  // Force legend outside of SVG by hiding it in SVG and recreating below
  '& .apexcharts-legend': {
    display: 'none !important', // Hide the SVG-rendered legend
  },
}));

const CustomLegendStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(1.5, 1),
  borderTop: `solid 1px ${theme.palette.divider}`,
  marginTop: theme.spacing(1),
  gap: theme.spacing(0.5),
  backgroundColor: 'transparent',
}));

const LegendItem = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  margin: theme.spacing(0.5, 1),
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}));

const LegendMarker = styled('span')<{ color: string }>(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: 12,
  backgroundColor: color,
  marginRight: 8,
  flexShrink: 0,
}));

const LegendText = styled('span')(({ theme }) => ({
  fontSize: '13px',
  lineHeight: '18px',
  color: theme.palette.text.primary,
}));

// ----------------------------------------------------------------------

interface Props extends CardProps {
  title?: string;
  subheader?: string;
  chartData: {
    label: string;
    value: number;
  }[];
  chartColors: string[][];
  labelColors: string[];
  customChartOptions?: any;
  isLoading?: boolean;
  totalValue?: number;
  showZeroValues?: boolean;
}

export default function DonutChart({
  title,
  subheader,
  chartColors,
  chartData,
  labelColors,
  customChartOptions = {},
  isLoading = false,
  totalValue,
  showZeroValues = false,
}: Props) {

  // Filter out items with zero or negative values, but keep all valid data
  const processedChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { 
        data: [{ label: 'All', value: 1 }], 
        colors: [['#E0E0E0', '#E0E0E0']], 
        labelColors: ['#9E9E9E'] 
      };
    }

    const validData = showZeroValues 
      ? chartData.filter((item) => item.value >= 0)
      : chartData.filter((item) => item.value > 0);

    // If all values are 0, show placeholder with grey circle
    if (validData.length === 0) {
      return { 
        data: [{ label: 'All', value: 1 }], 
        colors: [['#E0E0E0', '#E0E0E0']], 
        labelColors: ['#9E9E9E'] 
      };
    }

    return {
      data: validData,
      colors: chartColors.slice(0, validData.length),
      labelColors: labelColors.slice(0, validData.length),
    };
  }, [chartData, chartColors, labelColors]);

  const chartLabels = processedChartData.data.map((i) => i.label);
  const chartSeries = processedChartData.data.map((i) => i.value);
  const isNoData = chartLabels.length === 0 && chartLabels[0] === 'All' && chartSeries[0] === 0;

  const chartOptions = merge(
    BaseOptionChart(),
    merge(
      {
        labels: chartLabels,
        legend: {
          show: false, // Disable ApexCharts legend completely
        },
        chart: {
          height: 'auto',
          offsetY: 0,
        },
        responsive: [{
          breakpoint: 10000,
          options: {
            chart: {
              width: '100%',
            },
          },
        }],
        tooltip: {
          enabled: !isNoData, // Disable tooltip for empty state
          y: {
            formatter: (val: number) => {
              return fNumber(val);
            },
          },
        },
        states: {
          hover: {
            filter: {
              type: 'none', // Disable color change on hover
            },
          },
          active: {
            filter: {
              type: 'none', // Disable color change on active/click
            },
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            colorStops: processedChartData.colors.map((colors) => [
              { offset: 0, color: colors[0] },
              { offset: 100, color: colors[1] },
            ]),
            opacity: 1,
            opacityFrom: 1,
            opacityTo: 1,
            shadeIntensity: 1,
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '14px',
                  offsetY: -10,
                },
                value: {
                  show: true,
                  fontSize: '24px',
                  fontWeight: 600,
                  offsetY: 5,
                  formatter: (val: any) => fNumber(val),
                },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '14px',
                  fontWeight: 400,
                  formatter: (w: { globals: { seriesTotals: number[] } }) => {
                    // If showing "All" placeholder, display 0
                    if (isNoData) {
                      return '0';
                    }
                    // Use provided totalValue if available, otherwise sum the segments
                    if (totalValue !== undefined) {
                      return fNumber(totalValue);
                    }
                    const sum = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                    return fNumber(sum);
                  },
                },
              },
            },
          },
        },
        colors: processedChartData.labelColors,
        dataLabels: {
          enabled: false,
        },
      },
      customChartOptions
    )
  );

  return (
    <>
      {title && <CardHeader title={title} subheader={subheader} />}
      
      <ChartWrapperStyle dir="ltr">
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
              width: '100%',
            }}
          >
            <CircularProgress size={'6rem'} />
          </div>
        ) : (
          <>
            <ReactApexChart
              type="donut"
              series={chartSeries}
              options={chartOptions}
              height={250}
            />
            {/* Custom Legend Below Chart */}
            <CustomLegendStyle>
              {processedChartData.data.map((item, index) => (
                <LegendItem key={index}>
                  <LegendMarker color={processedChartData.labelColors[index]} />
                  <LegendText>{item.label}</LegendText>
                </LegendItem>
              ))}
            </CustomLegendStyle>
          </>
        )}
      </ChartWrapperStyle>
    </>
  );
}
